import {useBreadcrumbItem, useBreadcrumbs} from '@react-aria/breadcrumbs'
import {Link} from 'quickstart/components/content/Link'
import {Box} from 'quickstart/components/layout/Box'
import {
  Children,
  cloneElement,
  ComponentProps,
  forwardRef,
  Fragment,
  isValidElement,
  useRef,
} from 'react'

const Span = forwardRef<any, ComponentProps<typeof Box>>((props, ref) => (
  <Box as="span" {...props} ref={ref} />
))

const Separator = ({children = '>', ...props}: ComponentProps<typeof Box>) => (
  <Box as="span" px="xs" aria-hidden="true" {...props}>
    {children}
  </Box>
)

const List = (props: ComponentProps<typeof Box>) => (
  <Box as="ol" display="inline" listStyle="none" m="0" p="0" {...props} />
)

const Item = (props: ComponentProps<typeof Box>) => (
  <Box as="li" display="inline" {...props} />
)

type BreadcrumbsItemProps = Parameters<typeof useBreadcrumbItem>[0] &
  (
    | ({
        href: string
        linkVariant?: ComponentProps<typeof Link>['variant']
      } & Omit<ComponentProps<typeof Link>, 'variant'>)
    | ({
        href?: null | undefined | ''
      } & ComponentProps<typeof Span>)
  )

const BreadcrumbsItem = ({
  className,
  href,
  // @ts-expect-error
  linkVariant,
  ...props
}: BreadcrumbsItemProps) => {
  const [ElementType, elementType, elementProps] = href
    ? [Link, 'a', {...props, href, variant: linkVariant}]
    : [Span, 'span', props]
  const ref = useRef<any>()
  const {itemProps} = useBreadcrumbItem({elementType, ...props}, ref)
  return (
    <Item className={className}>
      <ElementType ref={ref} {...(elementProps as any)} {...itemProps} />
    </Item>
  )
}

type BreadcrumbsProps = Parameters<typeof useBreadcrumbs>[0] &
  Omit<ComponentProps<typeof Box>, 'children'> & {
    linkVariant?: string
  }

const _Breadcrumbs = ({
  children,
  linkVariant = 'breadcrumb',
  ...props
}: BreadcrumbsProps) => {
  const {navProps} = useBreadcrumbs({...props, children})
  const childArray = Children.toArray(children)
    .filter(Boolean)
    .map((child, i) => (
      <Fragment key={i}>
        {i ? <Separator /> : null}
        {/* @ts-expect-error */}
        {isValidElement(child) ? cloneElement(child, {linkVariant}) : child}
      </Fragment>
    ))
  return (
    <Box {...props} {...navProps}>
      <List>{childArray} </List>
    </Box>
  )
}

export const Breadcrumbs = Object.assign(_Breadcrumbs, {Item: BreadcrumbsItem})
